import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Eye, EyeOff, Info } from "lucide-react";
import { API_URL } from "../config/api";
import {
  clearForgotResetFromLocation,
  readForgotResetFromLocation,
} from "../utils/forgotPasswordLocation";
import {
  clearStoredAccessLink,
  consumeAccessLinkFromLocation,
  readAccessLinkFromLocation,
  readStoredAccessLink,
} from "../utils/accessLinkLocation";
import useAppFitScale from "../hooks/useAppFitScale";
import {
  TEXT_SCALE_FRAME_GROW,
  TEXT_SCALE_FRAME_GROW_X,
  applyTextScale,
} from "../constants/textScalePreference";
import vocabulariumBlason from "../assets/vocabularium_blason.png";
import AppCredit from "./AppCredit";
import Arabesque from "./Arabesque";
import MenuIconTooltip from "./MenuIconTooltip";
import {
  DISPLAY_NAME_MAX_LENGTH,
  DISPLAY_NAME_TOOLTIP,
  validateDisplayName,
} from "../utils/displayNameSanitize";
import {
  PASSWORD_MAX_LENGTH,
  PASSWORD_POLICY_TOOLTIP,
  validateAccountPassword,
} from "../utils/passwordPolicy";
import "./WordEditPanel.css";
import "../App.css";
import "./LoginScreen.css";

const BANNER_ERROR_MS = 5000;

function introForView(view, challenge, decideAction) {
  if (challenge?.challenge === "SOFTWARE_TOKEN_MFA") {
    return "Saisissez le code à 6 chiffres affiché par votre application d'authentification.";
  }
  if (challenge?.challenge === "DISPLAY_NAME_REQUIRED") {
    return "Choisissez le nom qui vous représentera dans Vocabularium.";
  }
  if (challenge) {
    return "Choisissez un nom et un mot de passe définitif pour ce compte.";
  }
  if (view === "request") {
    return "Indiquez l'adresse e-mail à laquelle vous souhaitez recevoir un accès.";
  }
  if (view === "requested") {
    return "Demande envoyée. Un e-mail suivra si elle est acceptée.";
  }
  if (view === "invite") {
    return "Choisissez un mot de passe et le nom qui vous représentera dans Vocabularium.";
  }
  if (view === "created") {
    return "Bienvenue dans Vocabularium !";
  }
  if (view === "decide") {
    return decideAction === "reject"
      ? "Confirmez le refus de cette demande d'accès."
      : "Confirmez l'acceptation de cette demande d'accès.";
  }
  if (view === "forgot") {
    return "Indiquez l'adresse e-mail de votre compte. Si un compte existe, un e-mail vous sera envoyé.";
  }
  if (view === "reset") {
    return "Saisissez le code reçu par e-mail, puis choisissez un nouveau mot de passe.";
  }
  if (view === "reset-done") {
    return "";
  }
  return "Tirez le meilleur parti de vos lectures !";
}

function LoginFloatingField({
  id,
  label,
  type = "text",
  value,
  onChange,
  autoComplete,
  required,
  tabIndex,
  maxLength,
  inputMode,
  autoCapitalize,
  passwordToggle = false,
  tooltip,
  tooltipAriaLabel,
  className = "",
  disabled = false,
  readOnly = false,
}) {
  const [focused, setFocused] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const filled = String(value ?? "").length > 0;
  const floated = focused || filled || readOnly;
  const inputType = passwordToggle ? (passwordVisible ? "text" : "password") : type;

  const handleFieldBlur = (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setFocused(false);
    if (passwordToggle) setPasswordVisible(false);
  };

  return (
    <div
      className={[
        "login-screen__field",
        floated ? "login-screen__field--floated" : "",
        passwordToggle ? "login-screen__field--password" : "",
        tooltip ? "login-screen__field--with-info" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      onBlur={handleFieldBlur}
    >
      <input
        id={id}
        className="login-screen__input"
        type={inputType}
        autoComplete={autoComplete}
        autoCapitalize={autoCapitalize}
        inputMode={inputMode}
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        required={required}
        tabIndex={tabIndex}
        maxLength={maxLength}
        disabled={disabled}
        readOnly={readOnly}
        placeholder=" "
        spellCheck={type === "email" || passwordToggle ? false : undefined}
      />
      <div className="login-screen__float-caption">
        <label className="login-screen__float-label" htmlFor={id}>
          {label}
        </label>
        {tooltip ? (
          <MenuIconTooltip tooltip={tooltip} placement="above" portal>
            <button
              type="button"
              className="vocab-info-button"
              aria-label={tooltipAriaLabel}
              tabIndex={tabIndex}
            >
              <Info size={13} strokeWidth={2} aria-hidden="true" />
            </button>
          </MenuIconTooltip>
        ) : null}
      </div>
      {passwordToggle ? (
        <button
          type="button"
          className="login-screen__peek"
          aria-label={
            passwordVisible ? "Masquer le mot de passe" : "Afficher le mot de passe"
          }
          aria-pressed={passwordVisible}
          tabIndex={tabIndex}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => setPasswordVisible((open) => !open)}
        >
          {passwordVisible ? (
            <Eye size={18} strokeWidth={1.75} aria-hidden="true" />
          ) : (
            <EyeOff size={18} strokeWidth={1.75} aria-hidden="true" />
          )}
        </button>
      ) : null}
    </div>
  );
}

function LoginPasswordSpacer() {
  return (
    <div
      className="login-screen__password-slot login-screen__password-slot--spacer"
      aria-hidden="true"
    >
      <div className="login-screen__field login-screen__field--password">
        <span className="login-screen__input">&nbsp;</span>
        <div className="login-screen__float-caption">
          <span className="login-screen__float-label">Votre mot de passe</span>
        </div>
      </div>
    </div>
  );
}

function peekAccessLink() {
  if (typeof window === "undefined") return null;
  return readAccessLinkFromLocation() || readStoredAccessLink();
}

function initialViewFromLocation() {
  const link = peekAccessLink();
  if (link?.kind === "invite") return "invite";
  if (link?.kind === "decide") return "decide";
  return "login";
}

function loginWavePath(baseY, amplitude = 5.5, periods = 10) {
  const width = 1000;
  const half = width / (periods * 2);
  let d = `M 0 ${baseY} Q ${half / 2} ${baseY - amplitude} ${half} ${baseY}`;
  for (let i = 2; i <= periods * 2; i += 1) {
    d += ` T ${half * i} ${baseY}`;
  }
  return d;
}

const LOGIN_WAVE_OFFSETS = [-11, 0, 11];

function LoginWaves() {
  return (
    <svg
      className="login-screen__waves"
      viewBox="0 0 1000 100"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      {LOGIN_WAVE_OFFSETS.map((offset) => (
        <path
          key={offset}
          d={loginWavePath(50 + offset)}
          fill="none"
          stroke="#39210d"
          strokeWidth="1.15"
          strokeLinecap="butt"
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </svg>
  );
}

const LOGIN_TEXT_SCALE = 1.25;

export default function LoginScreen({ onAuthenticated, initialChallenge = null }) {
  const appFit = useAppFitScale(TEXT_SCALE_FRAME_GROW, TEXT_SCALE_FRAME_GROW_X);

  useLayoutEffect(() => {
    applyTextScale(LOGIN_TEXT_SCALE);
  }, []);
  const [view, setView] = useState(initialViewFromLocation);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [inviteToken, setInviteToken] = useState(() => {
    const link = peekAccessLink();
    return link?.kind === "invite" ? link.token : "";
  });
  const [decideToken, setDecideToken] = useState(() => {
    const link = peekAccessLink();
    return link?.kind === "decide" ? link.token : "";
  });
  const [decideAction, setDecideAction] = useState(() => {
    const link = peekAccessLink();
    return link?.kind === "decide" ? link.action : "approve";
  });
  const [createdNameTaken, setCreatedNameTaken] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const errorTimerRef = useRef(null);

  const dismissError = () => {
    if (errorTimerRef.current) {
      window.clearTimeout(errorTimerRef.current);
      errorTimerRef.current = null;
    }
    setError("");
  };

  const showError = (message) => {
    const text = String(message || "").trim();
    if (errorTimerRef.current) {
      window.clearTimeout(errorTimerRef.current);
      errorTimerRef.current = null;
    }
    if (!text) {
      setError("");
      return;
    }
    setError(text);
    errorTimerRef.current = window.setTimeout(() => {
      setError("");
      errorTimerRef.current = null;
    }, BANNER_ERROR_MS);
  };

  const handleInput = (setter) => (event) => {
    dismissError();
    setter(event.target.value);
  };

  useEffect(() => {
    let cancelled = false;

    const openFromUrl = async () => {
      const accessLink = consumeAccessLinkFromLocation();
      if (accessLink) {
        setBusy(true);
        try {
          const res = await fetch(
            `${API_URL}/auth/access-preview?token=${encodeURIComponent(accessLink.token)}`
          );
          const json = await res.json().catch(() => ({}));
          if (cancelled) return;
          if (!res.ok) {
            clearStoredAccessLink();
            showError(json.message || "Ce lien n'est plus valable.");
            return;
          }
          if (json.email) setEmail(json.email);
          if (accessLink.kind === "invite") {
            setInviteToken(accessLink.token);
            setView("invite");
            return;
          }
          setDecideToken(accessLink.token);
          setDecideAction(accessLink.action);
          setView("decide");
        } catch {
          if (!cancelled) showError("Le serveur ne répond pas.");
        } finally {
          if (!cancelled) setBusy(false);
        }
        return;
      }

      const fromUrl = readForgotResetFromLocation();
      if (!fromUrl) return;
      if (fromUrl.email) setEmail(fromUrl.email);
      if (fromUrl.code) setCode(fromUrl.code);
      setView(fromUrl.email ? "reset" : "forgot");
      clearForgotResetFromLocation();
    };

    openFromUrl();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (initialChallenge?.challenge === "DISPLAY_NAME_REQUIRED") {
      setChallenge(initialChallenge);
      if (initialChallenge.email) setEmail(initialChallenge.email);
    }
  }, [initialChallenge]);

  useEffect(
    () => () => {
      if (errorTimerRef.current) window.clearTimeout(errorTimerRef.current);
    },
    []
  );

  const finishAuthenticated = async (json, fallbackEmail) => {
    const nextEmail = json.email || fallbackEmail;
    await onAuthenticated({
      email: nextEmail,
      username: json.email || nextEmail,
      sub: json.sub || "",
    });
  };

  const goLogin = () => {
    setView("login");
    setChallenge(null);
    dismissError();
    setInfo("");
    setCode("");
    setMfaCode("");
    setNewPassword("");
    setConfirmPassword("");
    setDisplayName("");
    setInviteToken("");
    setDecideToken("");
    setDecideAction("approve");
    clearStoredAccessLink();
  };

  const applyAuthResponse = async (json, fallbackEmail) => {
    if (
      json.challenge === "NEW_PASSWORD_REQUIRED" ||
      json.challenge === "DISPLAY_NAME_REQUIRED" ||
      json.challenge === "SOFTWARE_TOKEN_MFA"
    ) {
      setChallenge(json);
      if (json.challenge === "NEW_PASSWORD_REQUIRED") {
        setNewPassword("");
        setConfirmPassword("");
      }
      if (json.challenge === "SOFTWARE_TOKEN_MFA") {
        setMfaCode("");
      }
      if (json.message) showError(json.message);
      return true;
    }
    if (json.ok && json.sub) {
      await finishAuthenticated(json, fallbackEmail);
      return true;
    }
    return false;
  };

  const submitLogin = async (event) => {
    event.preventDefault();
    dismissError();
    setInfo("");
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        showError(json.message || "Connexion impossible.");
        return;
      }
      if (!(await applyAuthResponse(json, email))) {
        showError("Réponse de connexion inattendue.");
      }
    } catch (err) {
      showError(
        err instanceof TypeError
          ? "Le serveur ne répond pas."
          : err.message || "Le serveur ne répond pas."
      );
    } finally {
      setBusy(false);
    }
  };

  const submitMfaCode = async (event) => {
    event.preventDefault();
    dismissError();
    setInfo("");
    const nextCode = mfaCode.trim();
    if (!/^\d{6}$/.test(nextCode)) {
      showError("Code à 6 chiffres requis.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/auth/mfa`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: challenge?.email || email,
          session: challenge?.session || "",
          code: nextCode,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        showError(json.message || "Ce code n'est pas le bon.");
        return;
      }
      if (!(await applyAuthResponse(json, challenge?.email || email))) {
        showError("Réponse de connexion inattendue.");
      }
    } catch (err) {
      showError(
        err instanceof TypeError
          ? "Le serveur ne répond pas."
          : err.message || "Le serveur ne répond pas."
      );
    } finally {
      setBusy(false);
    }
  };

  const submitNewPassword = async (event) => {
    event.preventDefault();
    dismissError();
    const nameCheck = validateDisplayName(displayName);
    if (!nameCheck.ok) {
      showError(nameCheck.message);
      return;
    }
    const passwordCheck = validateAccountPassword(newPassword);
    if (!passwordCheck.ok) {
      showError(passwordCheck.message);
      return;
    }
    if (newPassword !== confirmPassword) {
      showError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/auth/new-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: challenge.email || email,
          password,
          newPassword,
          session: challenge.session,
          displayName,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        showError(json.message || "Impossible d'enregistrer le nouveau mot de passe.");
        return;
      }
      if (!(await applyAuthResponse(json, challenge.email || email))) {
        showError("Impossible d'enregistrer le nouveau mot de passe.");
      }
    } catch (err) {
      showError(
        err instanceof TypeError
          ? "Le serveur ne répond pas."
          : err.message || "Le serveur ne répond pas."
      );
    } finally {
      setBusy(false);
    }
  };

  const submitDisplayName = async (event) => {
    event.preventDefault();
    dismissError();
    const nameCheck = validateDisplayName(displayName);
    if (!nameCheck.ok) {
      showError(nameCheck.message);
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/auth/display-name`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ displayName }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        showError(json.message || "Impossible d'enregistrer ce nom.");
        return;
      }
      await finishAuthenticated(
        {
          email: challenge.email || email,
          sub: challenge.sub,
        },
        challenge.email || email
      );
    } catch (err) {
      showError(
        err instanceof TypeError
          ? "Le serveur ne répond pas."
          : err.message || "Le serveur ne répond pas."
      );
    } finally {
      setBusy(false);
    }
  };

  const requestResetEmail = async () => {
    const res = await fetch(`${API_URL}/auth/forgot-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: email.trim() }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(json.message || "Impossible d'envoyer la demande.");
    }
  };

  const submitForgot = async (event) => {
    event.preventDefault();
    dismissError();
    setInfo("");
    setBusy(true);
    try {
      await requestResetEmail();
      setView("reset");
    } catch (err) {
      showError(
        err instanceof TypeError
          ? "Le serveur ne répond pas."
          : err.message || "Le serveur ne répond pas."
      );
    } finally {
      setBusy(false);
    }
  };

  const submitAccessRequest = async (event) => {
    event.preventDefault();
    dismissError();
    setInfo("");
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/auth/access-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        showError(json.message || "Impossible d'enregistrer la demande.");
        return;
      }
      setView("requested");
    } catch {
      showError("Le serveur ne répond pas.");
    } finally {
      setBusy(false);
    }
  };

  const submitInvite = async (event) => {
    event.preventDefault();
    dismissError();
    const nameCheck = validateDisplayName(displayName);
    if (!nameCheck.ok) {
      showError(nameCheck.message);
      return;
    }
    const passwordCheck = validateAccountPassword(newPassword);
    if (!passwordCheck.ok) {
      showError(passwordCheck.message);
      return;
    }
    if (newPassword !== confirmPassword) {
      showError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    if (!inviteToken) {
      showError("Ce lien n'est plus valable.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/auth/complete-invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          token: inviteToken,
          password: newPassword,
          displayName,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        showError(json.message || "Impossible de créer le compte.");
        return;
      }
      if (json.email) setEmail(json.email);
      setNewPassword("");
      setConfirmPassword("");
      setDisplayName("");
      setInviteToken("");
      clearStoredAccessLink();
      setCreatedNameTaken(Boolean(json.nameTaken));
      setView("created");
    } catch {
      showError("Le serveur ne répond pas.");
    } finally {
      setBusy(false);
    }
  };

  const submitAccessDecide = async (event) => {
    event.preventDefault();
    dismissError();
    setInfo("");
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/auth/access-decide`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: decideToken,
          action: decideAction,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        showError(json.message || "Impossible de traiter cette demande.");
        return;
      }
      goLogin();
      setInfo(json.message || "La demande a été traitée.");
    } catch {
      showError("Le serveur ne répond pas.");
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (event) => {
    event.preventDefault();
    dismissError();
    const passwordCheck = validateAccountPassword(newPassword);
    if (!passwordCheck.ok) {
      showError(passwordCheck.message);
      return;
    }
    if (newPassword !== confirmPassword) {
      showError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/auth/confirm-forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: email.trim(),
          code: code.trim(),
          newPassword,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        showError(json.message || "Impossible d'enregistrer le mot de passe.");
        return;
      }
      setPassword("");
      setCode("");
      setNewPassword("");
      setConfirmPassword("");
      setView("reset-done");
    } catch {
      showError("Le serveur ne répond pas.");
    } finally {
      setBusy(false);
    }
  };

  const bannerText = error || introForView(view, challenge, decideAction);

  return (
    <div className={appFit.scaled ? "app-fit app-fit--scaled" : "app-fit app-fit--natural"}>
      <div
        className="app-fit__slot"
        style={
          appFit.scaled
            ? { width: appFit.slotWidth, height: appFit.slotHeight }
            : undefined
        }
      >
        <div
          className="app-fit__inner"
          style={
            appFit.scaled
              ? {
                  width: appFit.innerWidth,
                  height: appFit.innerHeight,
                  transform: `scale(${appFit.scale})`,
                }
              : undefined
          }
        >
          <div
            className="custom-pattern mode-login"
            style={
              appFit.scaled
                ? { width: appFit.frameWidth, height: appFit.frameHeight }
                : undefined
            }
          >
            <div className="divsvg">
              <Arabesque />
            </div>
            <div className="divsvg2">
              <Arabesque />
            </div>
            <div className="divsvg3">
              <Arabesque extendTail={0.45} />
            </div>
            <div className="divsvg4">
              <Arabesque extendTail={0.45} />
            </div>

            <div
              className={[
                "login-screen",
                view === "invite" ? "login-screen--can-scroll" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <div className="login-screen__card">
                <div
                  className={[
                    "login-screen__heraldry",
                    view === "invite" ? "login-screen__heraldry--invite" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <LoginWaves />
                  <div className="login-screen__blason-wrap">
                    <img
                      className="login-screen__blason"
                      src={vocabulariumBlason}
                      alt="Vocabularium"
                    />
                  </div>
                </div>
                <p
                  className={[
                    "login-screen__intro",
                    error ? "login-screen__intro--error" : "",
                    bannerText ? "" : "login-screen__intro--empty",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  role={error ? "alert" : undefined}
                >
                  {bannerText ? <span>{bannerText}</span> : null}
                </p>

                {challenge?.challenge === "SOFTWARE_TOKEN_MFA" ? (
                  <form className="login-screen__form" onSubmit={submitMfaCode}>
                    <LoginFloatingField
                      id="login-mfa-code"
                      label="Code d'authentification"
                      type="text"
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      value={mfaCode}
                      onChange={handleInput(setMfaCode)}
                      required
                      maxLength={6}
                    />
                    <LoginPasswordSpacer />
                    <button
                      type="submit"
                      className="word-edit-btn word-edit-btn--primary login-screen__submit"
                      disabled={busy}
                    >
                      {busy ? "Vérification…" : "Valider"}
                    </button>
                    <button type="button" className="login-screen__text-btn" onClick={goLogin}>
                      Retour à la connexion
                    </button>
                  </form>
                ) : challenge ? (
                  <form
                    className="login-screen__form"
                    onSubmit={
                      challenge.challenge === "DISPLAY_NAME_REQUIRED"
                        ? submitDisplayName
                        : submitNewPassword
                    }
                  >
                    <LoginFloatingField
                      id="login-display-name"
                      label="Votre nom"
                      type="text"
                      autoComplete="nickname"
                      autoCapitalize="words"
                      value={displayName}
                      onChange={handleInput(setDisplayName)}
                      required
                      maxLength={DISPLAY_NAME_MAX_LENGTH}
                      tooltip={DISPLAY_NAME_TOOLTIP}
                      tooltipAriaLabel="Règles du nom"
                    />
                    {challenge.challenge !== "DISPLAY_NAME_REQUIRED" ? (
                      <>
                        <LoginFloatingField
                          id="login-new-password"
                          label="Nouveau mot de passe"
                          passwordToggle
                          autoComplete="new-password"
                          value={newPassword}
                          onChange={handleInput(setNewPassword)}
                          required
                          maxLength={PASSWORD_MAX_LENGTH}
                          tooltip={PASSWORD_POLICY_TOOLTIP}
                          tooltipAriaLabel="Règles du mot de passe"
                        />
                        <LoginFloatingField
                          id="login-confirm-password"
                          label="Confirmez votre mot de passe"
                          passwordToggle
                          autoComplete="new-password"
                          value={confirmPassword}
                          onChange={handleInput(setConfirmPassword)}
                          required
                          maxLength={PASSWORD_MAX_LENGTH}
                          tooltip={PASSWORD_POLICY_TOOLTIP}
                          tooltipAriaLabel="Règles du mot de passe"
                        />
                      </>
                    ) : null}
                    <button
                      type="submit"
                      className="word-edit-btn word-edit-btn--primary login-screen__submit"
                      disabled={busy}
                    >
                      {busy ? "Enregistrement…" : "Enregistrer"}
                    </button>
                    <button
                      type="button"
                      className="login-screen__text-btn"
                      onClick={goLogin}
                    >
                      Retour à la connexion
                    </button>
                  </form>
                ) : null}

                {!challenge &&
                (view === "login" ||
                  view === "forgot" ||
                  view === "request" ||
                  view === "requested") ? (
                  <div className="login-screen__switch">
                    <form
                      className={
                        view === "login"
                          ? "login-screen__form"
                          : "login-screen__form login-screen__form--inactive"
                      }
                      onSubmit={submitLogin}
                      aria-hidden={view !== "login"}
                      inert={view !== "login" ? true : undefined}
                    >
                      <LoginFloatingField
                        id="login-email"
                        className="login-screen__email-field"
                        label="Votre adresse e-mail"
                        type="email"
                        autoComplete="username"
                        value={email}
                        onChange={handleInput(setEmail)}
                        tabIndex={view === "login" ? undefined : -1}
                        required={view === "login"}
                      />
                      <div className="login-screen__password-slot">
                        <LoginFloatingField
                          id="login-password"
                          label="Votre mot de passe"
                          passwordToggle
                          autoComplete="current-password"
                          value={password}
                          onChange={handleInput(setPassword)}
                          tabIndex={view === "login" ? undefined : -1}
                          required={view === "login"}
                        />
                      </div>
                      <button
                        type="submit"
                        className="word-edit-btn word-edit-btn--primary login-screen__submit"
                        disabled={busy || view !== "login"}
                        tabIndex={view === "login" ? undefined : -1}
                      >
                        {busy && view === "login" ? "Connexion…" : "Entrer"}
                      </button>
                      <div className="login-screen__links">
                        <button
                          type="button"
                          className="login-screen__text-btn"
                          tabIndex={view === "login" ? undefined : -1}
                          onClick={() => {
                            dismissError();
                            setInfo("");
                            setView("forgot");
                          }}
                        >
                          Mot de passe oublié ?
                        </button>
                        <button
                          type="button"
                          className="login-screen__text-btn"
                          tabIndex={view === "login" ? undefined : -1}
                          onClick={() => {
                            dismissError();
                            setInfo("");
                            setView("request");
                          }}
                        >
                          Demander un accès
                        </button>
                      </div>
                    </form>

                    <form
                      className={
                        view === "forgot"
                          ? "login-screen__form"
                          : "login-screen__form login-screen__form--inactive"
                      }
                      onSubmit={submitForgot}
                      aria-hidden={view !== "forgot"}
                      inert={view !== "forgot" ? true : undefined}
                    >
                      <LoginFloatingField
                        id="forgot-email"
                        className="login-screen__email-field"
                        label="Votre adresse e-mail"
                        type="email"
                        autoComplete="username"
                        value={email}
                        onChange={handleInput(setEmail)}
                        tabIndex={view === "forgot" ? undefined : -1}
                        required={view === "forgot"}
                      />
                      <LoginPasswordSpacer />
                      <button
                        type="submit"
                        className="word-edit-btn word-edit-btn--primary login-screen__submit"
                        disabled={busy || view !== "forgot"}
                        tabIndex={view === "forgot" ? undefined : -1}
                      >
                        {busy && view === "forgot" ? "Envoi…" : "Envoyer l'e-mail"}
                      </button>
                      <div className="login-screen__links">
                        <button
                          type="button"
                          className="login-screen__text-btn"
                          tabIndex={view === "forgot" ? undefined : -1}
                          onClick={goLogin}
                        >
                          Retour à la connexion
                        </button>
                      </div>
                    </form>

                    <form
                      className={
                        view === "request" || view === "requested"
                          ? "login-screen__form"
                          : "login-screen__form login-screen__form--inactive"
                      }
                      onSubmit={submitAccessRequest}
                      aria-hidden={view !== "request" && view !== "requested"}
                      inert={
                        view !== "request" && view !== "requested" ? true : undefined
                      }
                    >
                      {view === "requested" ? null : (
                        <>
                          <LoginFloatingField
                            id="request-email"
                            className="login-screen__email-field"
                            label="Votre adresse e-mail"
                            type="email"
                            autoComplete="username"
                            value={email}
                            onChange={handleInput(setEmail)}
                            tabIndex={view === "request" ? undefined : -1}
                            required={view === "request"}
                          />
                          <LoginPasswordSpacer />
                          <button
                            type="submit"
                            className="word-edit-btn word-edit-btn--primary login-screen__submit"
                            disabled={busy || view !== "request"}
                            tabIndex={view === "request" ? undefined : -1}
                          >
                            {busy && view === "request" ? "Envoi…" : "Envoyer la demande"}
                          </button>
                        </>
                      )}
                      <div className="login-screen__links">
                        <button
                          type="button"
                          className="login-screen__text-btn"
                          tabIndex={
                            view === "request" || view === "requested" ? undefined : -1
                          }
                          onClick={goLogin}
                        >
                          Retour à la connexion
                        </button>
                      </div>
                    </form>
                  </div>
                ) : null}

                {!challenge && view === "invite" ? (
                  <form className="login-screen__form" onSubmit={submitInvite}>
                    <LoginFloatingField
                      id="invite-email"
                      label="Votre adresse e-mail"
                      type="email"
                      value={email}
                      onChange={() => {}}
                      readOnly
                    />
                    <LoginFloatingField
                      id="invite-display-name"
                      label="Votre nom"
                      type="text"
                      autoComplete="nickname"
                      autoCapitalize="words"
                      value={displayName}
                      onChange={handleInput(setDisplayName)}
                      required
                      maxLength={DISPLAY_NAME_MAX_LENGTH}
                      tooltip={DISPLAY_NAME_TOOLTIP}
                      tooltipAriaLabel="Règles du nom"
                    />
                    <LoginFloatingField
                      id="invite-new-password"
                      label="Votre mot de passe"
                      passwordToggle
                      autoComplete="new-password"
                      value={newPassword}
                      onChange={handleInput(setNewPassword)}
                      required
                      maxLength={PASSWORD_MAX_LENGTH}
                      tooltip={PASSWORD_POLICY_TOOLTIP}
                      tooltipAriaLabel="Règles du mot de passe"
                    />
                    <LoginFloatingField
                      id="invite-confirm-password"
                      label="Confirmez votre mot de passe"
                      passwordToggle
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={handleInput(setConfirmPassword)}
                      required
                      maxLength={PASSWORD_MAX_LENGTH}
                      tooltip={PASSWORD_POLICY_TOOLTIP}
                      tooltipAriaLabel="Règles du mot de passe"
                    />
                    <button
                      type="submit"
                      className="word-edit-btn word-edit-btn--primary login-screen__submit"
                      disabled={busy}
                    >
                      {busy ? "Enregistrement…" : "Valider"}
                    </button>
                    <button type="button" className="login-screen__text-btn" onClick={goLogin}>
                      Retour à la connexion
                    </button>
                  </form>
                ) : null}

                {!challenge && view === "created" ? (
                  <div className="login-screen__form login-screen__created" role="status">
                    <p className="login-screen__created-text">
                      Votre compte est lié à <strong>{email}</strong>.
                    </p>
                    {createdNameTaken ? (
                      <p className="login-screen__created-text">
                        Le nom choisi a été pris entre-temps : un autre nom vous sera demandé
                        à votre première connexion.
                      </p>
                    ) : null}
                    <p className="login-screen__created-text">
                      Connectez-vous avec votre e-mail et le mot de passe que vous venez de
                      choisir.
                    </p>
                    <button
                      type="button"
                      className="word-edit-btn word-edit-btn--primary login-screen__submit"
                      onClick={() => {
                        goLogin();
                        setCreatedNameTaken(false);
                      }}
                    >
                      Se connecter
                    </button>
                  </div>
                ) : null}

                {!challenge && view === "decide" ? (
                  <form className="login-screen__form" onSubmit={submitAccessDecide}>
                    <LoginFloatingField
                      id="decide-email"
                      label="Adresse concernée"
                      type="email"
                      value={email}
                      onChange={() => {}}
                      readOnly
                    />
                    <button
                      type="submit"
                      className="word-edit-btn word-edit-btn--primary login-screen__submit"
                      disabled={busy}
                    >
                      {busy
                        ? "Envoi…"
                        : decideAction === "reject"
                          ? "Refuser"
                          : "Accepter"}
                    </button>
                    <button type="button" className="login-screen__text-btn" onClick={goLogin}>
                      Retour à la connexion
                    </button>
                  </form>
                ) : null}

                {!challenge && view === "reset-done" ? (
                  <div className="login-screen__form login-screen__created" role="status">
                    <p className="login-screen__created-text">
                      Votre nouveau mot de passe est bien pris en compte. Vous pouvez dès
                      maintenant vous connecter avec ce nouveau mot de passe.
                    </p>
                    <div className="login-screen__links">
                      <button type="button" className="login-screen__text-btn" onClick={goLogin}>
                        Retour à la connexion
                      </button>
                    </div>
                  </div>
                ) : null}

                {!challenge && view === "reset" ? (
                  <form className="login-screen__form" onSubmit={submitReset}>
                    <LoginFloatingField
                      id="reset-code"
                      label="Code reçu par e-mail"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={code}
                      onChange={handleInput(setCode)}
                      required
                    />
                    <LoginFloatingField
                      id="reset-new-password"
                      label="Nouveau mot de passe"
                      passwordToggle
                      autoComplete="new-password"
                      value={newPassword}
                      onChange={handleInput(setNewPassword)}
                      required
                      maxLength={PASSWORD_MAX_LENGTH}
                      tooltip={PASSWORD_POLICY_TOOLTIP}
                      tooltipAriaLabel="Règles du mot de passe"
                    />
                    <LoginFloatingField
                      id="reset-confirm-password"
                      label="Confirmez votre mot de passe"
                      passwordToggle
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={handleInput(setConfirmPassword)}
                      required
                      maxLength={PASSWORD_MAX_LENGTH}
                      tooltip={PASSWORD_POLICY_TOOLTIP}
                      tooltipAriaLabel="Règles du mot de passe"
                    />
                    <button
                      type="submit"
                      className="word-edit-btn word-edit-btn--primary login-screen__submit"
                      disabled={busy}
                    >
                      {busy ? "Enregistrement…" : "Enregistrer"}
                    </button>
                    <div className="login-screen__links">
                      <button type="button" className="login-screen__text-btn" onClick={goLogin}>
                        Retour à la connexion
                      </button>
                    </div>
                  </form>
                ) : null}

                {info ? <p className="login-screen__info">{info}</p> : null}
              </div>
            </div>
            <AppCredit />
          </div>
        </div>
      </div>
    </div>
  );
}
